import type { ReactNode } from "react";
import { CloudOff, RefreshCw } from "lucide-react";
import { Button } from "./components";
import "./analytics.css";

const Block = ({ className = "" }: { className?: string }) => (
  <div className={`skeleton ${className}`.trim()} aria-hidden="true" />
);

type StatCardProps = {
  label: string;
  /** A number or short value; null shows a quiet dash with the hint below. */
  value: ReactNode | null;
  hint?: ReactNode;
  /** Dates and other worded values use a smaller type than numbers. */
  kind?: "number" | "text";
  accent?: boolean;
  loading?: boolean;
  title?: string;
  className?: string;
  children?: ReactNode;
};

/** One analytics figure. Missing values never take the large number type. */
export const StatCard = ({
  label,
  value,
  hint,
  kind = "number",
  accent = false,
  loading = false,
  title,
  className,
  children,
}: StatCardProps) => {
  const missing = value === null || value === undefined || value === "";
  return (
    <article
      className={["analytics-stat", className].filter(Boolean).join(" ")}
      title={title}
      aria-busy={loading || undefined}
    >
      <span>{label}</span>
      {loading ? (
        <Block className="analytics-stat-value-skeleton" />
      ) : (
        <strong
          className={[
            missing ? "is-empty" : kind === "text" ? "is-text" : "",
            accent && !missing ? "is-accent" : "",
          ].filter(Boolean).join(" ") || undefined}
        >
          {missing ? "—" : value}
        </strong>
      )}
      {loading ? <Block className="analytics-stat-hint-skeleton" /> : hint !== undefined && <small>{hint}</small>}
      {!loading && children}
    </article>
  );
};

const transientCodes = /(^|\/)(internal|unknown|unavailable|deadline-exceeded|resource-exhausted|cancelled|aborted)$/;
const bareCodeMessage = /^(internal|unknown|unavailable|deadline[- ]exceeded|resource[- ]exhausted|cancelled|aborted)$/i;

/** A calm sentence for a section that could not load; raw codes never reach the page. */
export const analyticsLoadErrorMessage = (reason: unknown): string => {
  const code = String((reason as { code?: unknown } | null)?.code ?? "");
  const message = reason instanceof Error ? reason.message.trim() : "";
  if (/(^|\/)(permission-denied|unauthenticated)$/.test(code)) {
    return "Your session may have expired. Sign in again to see this section.";
  }
  if (!message || transientCodes.test(code) || bareCodeMessage.test(message)) {
    return "This section could not load right now. Your data is safe. Try again in a moment.";
  }
  return message;
};

/** Inline, muted notice used in place of a section that could not load. */
export const SectionUnavailable = ({
  title,
  message,
  onRetry,
  retrying = false,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
  retrying?: boolean;
}) => (
  <div className="analytics-unavailable" role="status">
    <CloudOff size={18} aria-hidden="true" />
    <div>
      <strong>{title}</strong>
      <p>{message}</p>
    </div>
    {onRetry && (
      <Button
        type="button"
        tone="quiet"
        icon={<RefreshCw size={14} aria-hidden="true" />}
        onClick={onRetry}
        disabled={retrying}
      >
        Try again
      </Button>
    )}
  </div>
);

/** Stat cards in the same grid and sizes as the loaded figures. */
export const StatGridSkeleton = ({ scope }: { scope: "event" | "account" }) => (
  <section className={`analytics-stats is-${scope}-scope`} aria-hidden="true">
    {Array.from({ length: scope === "event" ? 8 : 6 }, (_, index) => (
      <article className="analytics-stat" key={index}>
        <Block className="analytics-stat-label-skeleton" />
        <Block className="analytics-stat-value-skeleton" />
        <Block className="analytics-stat-hint-skeleton" />
      </article>
    ))}
  </section>
);

/** The cost breakdown strip while its figures load. */
export const CostCardsSkeleton = ({ count = 4 }: { count?: number }) => (
  <div className="analytics-cost-skeleton" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }} aria-hidden="true">
    {Array.from({ length: count }, (_, index) => (
      <div key={index}>
        <Block className="analytics-stat-label-skeleton" />
        <Block className="analytics-cost-value-skeleton" />
        <Block className="analytics-stat-hint-skeleton" />
      </div>
    ))}
  </div>
);

/** Gallery download figures and charts while they load. */
export const DownloadsSkeleton = () => (
  <div className="analytics-downloads-skeleton" aria-hidden="true">
    <div className="analytics-downloads-skeleton-kpis">
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index}>
          <Block className="analytics-downloads-icon-skeleton" />
          <Block className="analytics-stat-label-skeleton" />
          <Block className="analytics-downloads-value-skeleton" />
          <Block className="analytics-stat-hint-skeleton" />
        </div>
      ))}
    </div>
    <div className="analytics-downloads-skeleton-detail">
      <Block />
      <Block />
    </div>
  </div>
);

/** Health and sharing channel panels while event figures load. */
const HealthSkeleton = () => (
  <div className="analytics-grid" aria-hidden="true">
    {[0, 1].map((panel) => (
      <section className="panel analytics-panel-skeleton" key={panel}>
        <Block className="analytics-panel-title-skeleton" />
        <Block className="analytics-panel-copy-skeleton" />
        {[0, 1, 2].map((row) => <Block className="analytics-panel-row-skeleton" key={row} />)}
      </section>
    ))}
  </div>
);

/** Everything below the event and date pickers, shaped like the loaded page. */
export const AnalyticsBodySkeleton = ({
  scope = "event",
  announce = true,
}: {
  scope?: "event" | "account";
  /** False inside another loading region that already announces itself. */
  announce?: boolean;
}) => (
  <div
    className="analytics-body-skeleton"
    {...(announce ? { role: "status", "aria-live": "polite" as const, "aria-busy": true } : { "aria-hidden": true })}
  >
    {announce && <span className="sr-only">Loading event analytics…</span>}
    <StatGridSkeleton scope={scope} />
    <section className="analytics-cost-cards" aria-hidden="true"><CostCardsSkeleton /></section>
    <HealthSkeleton />
    <section className="panel gallery-download-analytics" aria-hidden="true">
      <Block className="analytics-panel-title-skeleton" />
      <Block className="analytics-panel-copy-skeleton" />
      <DownloadsSkeleton />
    </section>
  </div>
);

/** The Analytics page from first paint: header, pickers and body placeholders. */
export const AnalyticsRouteSkeleton = () => (
  <div className="skeleton-page analytics-route-skeleton" aria-hidden="true">
    <Block className="skeleton-title" />
    <Block className="skeleton-meta" />
    <Block className="analytics-context-skeleton" />
    <AnalyticsBodySkeleton announce={false} />
  </div>
);
